import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import './PractitionerProfile.css';

const PractitionerProfile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editingBasic, setEditingBasic] = useState(false);
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});

  const timezoneOptions = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
    'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Dublin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok', 'Asia/Manila', 'Asia/Jakarta',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
    'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City',
    'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos'
  ];

  const learningInterests = [
    'Interview Preparation',
    'Public Speaking',
    'Dating/Flirting',
    'Business Communication',
    'Language Practice',
    'Social Skills',
    'Presentation Skills',
    'Networking',
    'Confidence Building',
    'Accent Training'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese',
    'Japanese', 'Korean', 'Arabic', 'Russian', 'Hindi', 'Dutch', 'Swedish',
    'Norwegian', 'Polish', 'Turkish', 'Hebrew', 'Thai', 'Vietnamese'
  ];

  const genderOptions = [
    'Male',
    'Female',
    'Prefer not to say'
  ];

  const ageBracketOptions = [
    '18-25',
    '26-35',
    '36-45',
    '46-55',
    '56-65',
    '65+'
  ];

  useEffect(() => {
    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser]);

  const fetchProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const profileData = docSnap.data();
        setProfile(profileData);
        setEditData({
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          bio: profileData.practitionerBio || '',
          learningInterests: profileData.learningInterests || [],
          languages: profileData.languages || [],
          timezone: profileData.timezone || '',
          gender: profileData.gender || '',
          ageBracket: profileData.ageBracket || '',
          preferredProviderGender: profileData.preferredProviderGender || '',
          experienceLevel: profileData.experienceLevel || '',
          goals: profileData.goals || '',
          availability: profileData.availability || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field, value) => {
    setEditData(prev => {
      const currentValues = prev[field] || [];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [field]: currentValues.filter(item => item !== value)
        };
      } else {
        return {
          ...prev,
          [field]: [...currentValues, value]
        };
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const docRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(docRef, {
        firstName: editData.firstName,
        lastName: editData.lastName,
        practitionerBio: editData.bio,
        learningInterests: editData.learningInterests,
        languages: editData.languages,
        timezone: editData.timezone,
        gender: editData.gender,
        ageBracket: editData.ageBracket,
        preferredProviderGender: editData.preferredProviderGender,
        experienceLevel: editData.experienceLevel,
        goals: editData.goals,
        availability: editData.availability,
        practitionerProfileComplete: true,
        updatedAt: serverTimestamp()
      });

      setProfile(prev => ({ 
        ...prev, 
        firstName: editData.firstName,
        lastName: editData.lastName,
        practitionerBio: editData.bio,
        learningInterests: editData.learningInterests,
        languages: editData.languages,
        timezone: editData.timezone,
        gender: editData.gender,
        ageBracket: editData.ageBracket,
        preferredProviderGender: editData.preferredProviderGender,
        experienceLevel: editData.experienceLevel,
        goals: editData.goals,
        availability: editData.availability,
        practitionerProfileComplete: true 
      }));
      setEditingBasic(false);
      setEditingPreferences(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingBasic(false);
    setEditingPreferences(false);
    // Reset edit data to original profile values
    if (profile) {
      setEditData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        bio: profile.practitionerBio || '',
        learningInterests: profile.learningInterests || [],
        languages: profile.languages || [],
        timezone: profile.timezone || '',
        gender: profile.gender || '',
        ageBracket: profile.ageBracket || '',
        preferredProviderGender: profile.preferredProviderGender || '',
        experienceLevel: profile.experienceLevel || '',
        goals: profile.goals || '',
        availability: profile.availability || ''
      });
    }
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="practitioner-profile">
      <div className="profile-container">
        <div className="profile-header">
          <div className="header-content">
            <h2>My Practitioner Profile</h2>
            <p className="header-subtitle">
              Help us match you with the perfect providers by telling us about your learning goals and preferences
            </p>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-section">
            <div className="section-header">
              <h3>Basic Information</h3>
              {!editingBasic && (
                <button onClick={() => setEditingBasic(true)} className="edit-btn">
                  Edit Profile
                </button>
              )}
            </div>
            
            {editingBasic ? (
              <div className="edit-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={editData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={editData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Bio</label>
                  <textarea
                    value={editData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell us a little about yourself and your background..."
                    rows={4}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      value={editData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    >
                      <option value="">Select gender</option>
                      {genderOptions.map(gender => (
                        <option key={gender} value={gender}>{gender}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Age Bracket</label>
                    <select
                      value={editData.ageBracket}
                      onChange={(e) => handleInputChange('ageBracket', e.target.value)}
                    >
                      <option value="">Select age bracket</option>
                      {ageBracketOptions.map(age => (
                        <option key={age} value={age}>{age}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Timezone</label>
                  <select
                    value={editData.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                  >
                    <option value="">Select timezone</option>
                    {timezoneOptions.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
                
                <div className="section-actions">
                  <button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="save-btn"
                  >
                    {saving ? 'Saving...' : '💾 Save Changes'}
                  </button>
                  <button 
                    onClick={handleCancel}
                    disabled={saving}
                    className="cancel-btn"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="profile-display">
                <div className="profile-info-grid">
                  <div className="info-column">
                    <div className="info-item">
                      <strong>Name:</strong> {profile?.firstName} {profile?.lastName}
                    </div>
                    <div className="info-item">
                      <strong>Gender:</strong> {profile?.gender || 'Not specified'}
                    </div>
                    <div className="info-item">
                      <strong>Timezone:</strong> {profile?.timezone || 'Not specified'}
                    </div>
                  </div>
                  <div className="info-column">
                    <div className="info-item">
                      <strong>Bio:</strong> {profile?.practitionerBio || 'Not specified'}
                    </div>
                    <div className="info-item">
                      <strong>Age Bracket:</strong> {profile?.ageBracket || 'Not specified'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="profile-section">
            <div className="section-header">
              <h3>Learning Preferences</h3>
              {!editingPreferences && (
                <button onClick={() => setEditingPreferences(true)} className="edit-btn">
                  Edit Preferences
                </button>
              )}
            </div>
            
            {editingPreferences ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>What are you interested in learning?</label>
                  <div className="multi-select">
                    {learningInterests.map(interest => (
                      <label key={interest} className="checkbox-label">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={editData.learningInterests?.includes(interest)}
                          onChange={() => handleMultiSelectChange('learningInterests', interest)}
                        />
                        <span className="checkbox-text">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Languages you speak</label>
                  <div className="multi-select">
                    {languageOptions.map(language => (
                      <label key={language} className="checkbox-label">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={editData.languages?.includes(language)}
                          onChange={() => handleMultiSelectChange('languages', language)}
                        />
                        <span className="checkbox-text">{language}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Experience Level</label>
                  <select
                    value={editData.experienceLevel}
                    onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                  >
                    <option value="">Select experience level</option>
                    <option value="Beginner">Beginner - Just starting out</option>
                    <option value="Intermediate">Intermediate - Some experience</option>
                    <option value="Advanced">Advanced - Experienced but want to improve</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Preferred Provider Gender</label>
                  <select
                    value={editData.preferredProviderGender}
                    onChange={(e) => handleInputChange('preferredProviderGender', e.target.value)}
                  >
                    <option value="">No preference</option>
                    <option value="Male">Male providers</option>
                    <option value="Female">Female providers</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Learning Goals</label>
                  <textarea
                    value={editData.goals}
                    onChange={(e) => handleInputChange('goals', e.target.value)}
                    placeholder="What specific goals do you want to achieve? (e.g., land a job interview, improve public speaking, feel more confident in social situations)"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Availability Notes</label>
                  <textarea
                    value={editData.availability}
                    onChange={(e) => handleInputChange('availability', e.target.value)}
                    placeholder="Any notes about your availability, preferred times, or scheduling preferences..."
                    rows={2}
                  />
                </div>
                
                <div className="section-actions">
                  <button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="save-btn"
                  >
                    {saving ? 'Saving...' : '💾 Save Changes'}
                  </button>
                  <button 
                    onClick={handleCancel}
                    disabled={saving}
                    className="cancel-btn"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="profile-display">
                <div className="info-item">
                  <strong>Learning Interests:</strong>
                  {profile?.learningInterests?.length > 0 ? (
                    <div className="tags">
                      {profile.learningInterests.map(interest => (
                        <span key={interest} className="tag">{interest}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="not-specified">Not specified</span>
                  )}
                </div>
                
                <div className="info-item">
                  <strong>Languages:</strong>
                  {profile?.languages?.length > 0 ? (
                    <div className="tags">
                      {profile.languages.map(language => (
                        <span key={language} className="tag">{language}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="not-specified">Not specified</span>
                  )}
                </div>
                
                <div className="info-item">
                  <strong>Experience Level:</strong> {profile?.experienceLevel || 'Not specified'}
                </div>
                
                <div className="info-item">
                  <strong>Preferred Provider Gender:</strong> {profile?.preferredProviderGender || 'No preference'}
                </div>
                
                <div className="info-item">
                  <strong>Learning Goals:</strong> {profile?.goals || 'Not specified'}
                </div>
                
                <div className="info-item">
                  <strong>Availability:</strong> {profile?.availability || 'Not specified'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerProfile;